import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, X, Heart, Users, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import JoinzyLogo from './joinzy_new.png'; // Nokta-eğik çizgi "burada" demek
// Bubble data with positions - moved away from center text
const bubbleData = [
  { id: 1, type: 'post', x: 8, y: 20, delay: 0, avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', name: 'Emma', age: 24 },
  { id: 2, type: 'moment', x: 88, y: 18, delay: 0.5, avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', name: 'Alex', age: 28 },
  { id: 3, type: 'post', x: 92, y: 60, delay: 1, avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', name: 'Sofia', age: 26 },
  { id: 4, type: 'moment', x: 5, y: 70, delay: 1.5, avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', name: 'Marcus', age: 30 },
  { id: 5, type: 'post', x: 18, y: 85, delay: 2, avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', name: 'Mia', age: 23 },
  { id: 6, type: 'moment', x: 82, y: 80, delay: 2.5, avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', name: 'Lucas', age: 27 },
];

export const Hero = () => {
  const canvasRef = useRef(null);
  const { t } = useLanguage();
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Canvas animation for the matrix grid
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let nodes = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes();
    };

    const initNodes = () => {
      nodes = [];
      const nodeCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 2 + 1,
        });
      }
    };

    const drawNode = (node) => {
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 229, 255, 0.6)';
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius * 2, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius * 3);
      gradient.addColorStop(0, 'rgba(0, 229, 255, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 229, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    const drawConnections = () => {
      const connectionDistance = 150;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.3;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0, 128, 128, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    };

    const updateNodes = () => {
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
      });
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(15, 17, 21, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      drawConnections();
      nodes.forEach(drawNode);
      updateNodes();
      
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    ctx.fillStyle = '#0F1115';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const handleBubbleClick = (bubble) => {
    setSelectedBubble(bubble);
  };

  const bubbleContent = {
    1: { key: 'bubbles.post1', tags: ['#gym', '#fitness'] },
    2: { key: 'bubbles.moment1', tags: ['#travel', '#bali'] },
    3: { key: 'bubbles.post2', tags: ['#concert', '#music'] },
    4: { key: 'bubbles.moment2', tags: ['#hiking', '#nature'] },
    5: { key: 'bubbles.post3', tags: ['#coffee', '#dating'] },
    6: { key: 'bubbles.moment3', tags: ['#party', '#newyear'] },
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#0F1115]" data-testid="hero-section">
      {/* Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        data-testid="hero-canvas"
      />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0F1115]" />
      <div className="absolute inset-0 bg-gradient-radial from-[#008080]/10 via-transparent to-transparent" />

      {/* Floating Bubbles - Only on desktop or positioned away from center on mobile */}
      {bubbleData.map((bubble) => (
        <FloatingBubble
          key={bubble.id}
          bubble={bubble}
          onClick={() => handleBubbleClick(bubble)}
          content={t(bubbleContent[bubble.id].key)}
          tags={bubbleContent[bubble.id].tags}
          isMobile={isMobile}
        />
      ))}

      {/* Hero Content - z-index lower than bubbles so bubbles are clickable */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 pt-20 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center max-w-4xl mx-auto pointer-events-auto"
        >
          {/* The End of Swiping - Simple text tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF]/70 mb-6"
            data-testid="hero-tagline"
          >
            {t('hero.tagline')}
          </motion.p>

          {/* Main Title */}
          <h1
            className="font-montserrat font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight tracking-tight mb-6"
            data-testid="hero-title"
          >
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p
            className="font-poppins text-base sm:text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-4 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </p>
          
          {/* 60ms Translation Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-[#00E5FF]/30 rounded-full mb-8"
          >
            <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse" />
            <span className="font-mono text-xs text-white/70">{t('hero.translationBadge')}</span>
            <span className="px-2 py-0.5 bg-[#00E5FF]/20 text-[#00E5FF] text-xs rounded-full">NEW</span>
          </motion.div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" id="download">
            <motion.a
              href="#"
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(0, 128, 128, 0.6)' }}
              whileTap={{ scale: 0.95 }}
              className="relative px-8 py-4 rounded-full font-poppins font-semibold text-white overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #008080 0%, #004D4D 100%)',
                boxShadow: '0 0 30px rgba(0, 128, 128, 0.4)',
              }}
              data-testid="hero-cta-button"
            >
              <span className="relative z-10">{t('hero.cta')}</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#00E5FF]/0 via-[#00E5FF]/30 to-[#00E5FF]/0"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6 }}
              />
            </motion.a>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Full-screen Overlay for Post/Moment Detail */}
      <AnimatePresence>
        {selectedBubble && (
          <BubbleOverlay
            bubble={selectedBubble}
            content={t(bubbleContent[selectedBubble.id].key)}
            tags={bubbleContent[selectedBubble.id].tags}
            onClose={() => setSelectedBubble(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const FloatingBubble = ({ bubble, onClick, content, tags, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [0, -15, 0],
      }}
      transition={{
        opacity: { delay: bubble.delay, duration: 0.5 },
        scale: { delay: bubble.delay, duration: 0.5 },
        y: { delay: bubble.delay + 0.5, duration: 3 + Math.random() * 2, repeat: Infinity, ease: 'easeInOut' },
      }}
      style={{
        left: `${bubble.x}%`,
        top: `${bubble.y}%`,
      }}
      className="absolute z-40 cursor-pointer"
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      data-testid={`bubble-${bubble.id}`}
    >
      {/* Bubble */}
      <motion.div
        animate={{
          width: isHovered ? 220 : 56,
          height: isHovered ? 'auto' : 56,
        }}
        className={`relative rounded-2xl overflow-hidden backdrop-blur-md border transition-all duration-300 ${
          bubble.type === 'post'
            ? 'border-[#00E5FF]/50 bg-[#0F1115]/90'
            : 'border-[#10B981]/50 bg-[#0F1115]/90'
        }`}
        style={{
          boxShadow: isHovered
            ? `0 0 30px ${bubble.type === 'post' ? 'rgba(0, 229, 255, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`
            : `0 0 15px ${bubble.type === 'post' ? 'rgba(0, 229, 255, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`,
        }}
      >
        {!isHovered ? (
          <img
            src={bubble.avatar}
            alt="User"
            className="w-14 h-14 rounded-2xl object-cover"
          />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <img
                src={bubble.avatar}
                alt="User"
                className="w-10 h-10 rounded-full object-cover border-2"
                style={{ borderColor: bubble.type === 'post' ? '#00E5FF' : '#10B981' }}
              />
              <div>
                <p className="text-white text-sm font-semibold">{bubble.name}, {bubble.age}</p>
                <span className={`text-[10px] font-mono uppercase tracking-wider ${
                  bubble.type === 'post' ? 'text-[#00E5FF]' : 'text-[#10B981]'
                }`}>
                  {bubble.type === 'post' ? 'POST' : 'MOMENT'}
                </span>
              </div>
            </div>
            <p className="text-white/80 text-xs font-poppins mb-2 line-clamp-2">{content}</p>
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, i) => (
                <span key={i} className={`px-2 py-0.5 text-[10px] rounded-full ${
                  bubble.type === 'post' 
                    ? 'bg-[#00E5FF]/20 text-[#00E5FF]' 
                    : 'bg-[#10B981]/20 text-[#10B981]'
                }`}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Pulse effect */}
      <motion.div
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0, 0.5],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          delay: bubble.delay,
        }}
        className={`absolute inset-0 rounded-2xl pointer-events-none ${
          bubble.type === 'post' ? 'bg-[#00E5FF]/20' : 'bg-[#10B981]/20'
        }`}
        style={{ width: 56, height: 56 }}
      />
    </motion.div>
  );
};

// Full Overlay showing Post or Moment like in the app
const BubbleOverlay = ({ bubble, content, tags, onClose }) => {
  const isPost = bubble.type === 'post';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bubble-overlay"
    >
      <motion.div
        initial={{ scale: 0.9, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm"
      >
        {/* Phone Frame */}
        <div className="relative bg-[#1A1A1A] rounded-[2.5rem] p-2 shadow-2xl border border-white/10">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-10" />
          
          {/* Screen */}
          <div className="bg-[#0B0E14] rounded-[2rem] overflow-hidden" style={{ aspectRatio: '9/18' }}>
            {isPost ? (
              <PostView bubble={bubble} content={content} tags={tags} onClose={onClose} />
            ) : (
              <MomentView bubble={bubble} content={content} tags={tags} onClose={onClose} />
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Post View - matches the app screenshot style
const PostView = ({ bubble, content, tags, onClose }) => (
  <div className="h-full flex flex-col">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-3 pt-8">
   <img 
  src={JoinzyLogo} // Tırnak işaretlerini kaldırdık, süslü parantez içine aldık
  alt="Joinzy"
  className="h-8 w-auto object-contain" // w-auto ekledik ki logo basık durmasın
/>
      <button onClick={onClose} className="p-1.5 rounded-full bg-white/10">
        <X className="w-4 h-4 text-white/70" />
      </button>
    </div>
    
    {/* Main Content */}
    <div className="flex-1 relative">
      <img 
        src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=800&fit=crop"
        alt="Post"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/30 to-transparent" />
      
      {/* Post Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex items-center gap-3 mb-3">
          <img 
            src={bubble.avatar}
            alt={bubble.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-[#00E5FF]"
          />
          <div>
            <h3 className="font-montserrat font-bold text-white text-xl">
              {bubble.name} <span className="font-normal text-white/60">{bubble.age}</span>
            </h3>
            <span className="text-[#00E5FF] text-xs font-mono">POST</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map((tag, i) => (
            <span key={i} className="px-3 py-1 bg-[#008080] text-white text-xs rounded-full">
              {tag}
            </span>
          ))}
        </div>
        
        <p className="text-white/70 text-xs mb-3">Looking for</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="px-3 py-1.5 border border-[#00E5FF] text-[#00E5FF] text-xs rounded-full">♂ Male</span>
          <span className="px-3 py-1.5 border border-pink-400 text-pink-400 text-xs rounded-full">♀ Female</span>
          <span className="px-3 py-1.5 border border-[#10B981] text-[#10B981] text-xs rounded-full">👥 Any</span>
        </div>
        
        <div className="flex items-center gap-4 text-white/50 text-xs">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> 18-35</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> less than 10 km</span>
        </div>
        
        <div className="flex items-center justify-between mt-4">
          <Heart className="w-6 h-6 text-white/60" />
          <button className="px-6 py-2 bg-[#008080] rounded-full text-white text-sm font-semibold flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Connect
          </button>
        </div>
      </div>
    </div>
    
    {/* Bottom Nav */}
    <div className="flex items-center justify-around py-3 bg-white">
      {/* Home - Active */}
      <div className="p-2 bg-[#008080]/10 rounded-xl">
        <svg className="w-6 h-6 text-[#008080]" fill="currentColor" viewBox="0 0 24 24">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
      </div>
      {/* Moments */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>
      {/* Plus Button */}
      <div className="w-12 h-12 rounded-full bg-[#008080] flex items-center justify-center shadow-lg border-4 border-white">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
        </svg>
      </div>
      {/* Chat */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
      </svg>
      {/* Profile */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    </div>
  </div>
);

// Moment View - matches the app screenshot style (like a tweet/story)
const MomentView = ({ bubble, content, tags, onClose }) => (
  <div className="h-full flex flex-col bg-white">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-3 pt-8 border-b border-gray-100">
      <button onClick={onClose} className="text-gray-400">
        <X className="w-5 h-5" />
      </button>
      <span className="font-montserrat font-bold text-[#222] text-lg">Moments</span>
      <span className="w-5" />
    </div>
    
    {/* Moment Content */}
    <div className="flex-1 overflow-auto">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <img 
            src={bubble.avatar}
            alt={bubble.name}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-[#222]">{bubble.name}, {bubble.age}</span>
              <span className="text-gray-400 text-xs">· 2h ago</span>
            </div>
            <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
              <MapPin className="w-3 h-3" /> London, UK
            </div>
            
            <p className="text-[#222] text-base mb-3">{content}</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-[#008080]/10 text-[#008080] text-sm rounded-full font-medium">
                  {tag}
                </span>
              ))}
            </div>
            
            <p className="text-gray-500 text-xs mb-2">Looking for</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 border border-[#00E5FF] text-[#00E5FF] text-xs rounded-full">♂ Male</span>
              <span className="px-3 py-1 border border-pink-400 text-pink-400 text-xs rounded-full">♀ Female</span>
              <span className="px-3 py-1 border border-[#10B981] text-[#10B981] text-xs rounded-full">👥 Any</span>
            </div>
            
            <div className="flex items-center gap-6 text-gray-400 text-sm pt-2 border-t border-gray-100">
              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> 18-45</span>
              <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> 12</span>
              <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> 3</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Button */}
      <div className="px-4 pb-4">
        <button className="w-full py-3 bg-[#008080] rounded-xl text-white font-semibold flex items-center justify-center gap-2">
          <Calendar className="w-5 h-5" /> Join this Moment
        </button>
      </div>
    </div>
    
    {/* Bottom Nav */}
    <div className="flex items-center justify-around py-3 border-t border-gray-100">
      {/* Home */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
      </svg>
      {/* Moments - Active */}
      <div className="p-2 bg-[#008080]/10 rounded-xl">
        <svg className="w-6 h-6 text-[#008080]" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      </div>
      {/* Plus Button */}
      <div className="w-12 h-12 rounded-full bg-[#008080] flex items-center justify-center shadow-lg border-4 border-white">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
        </svg>
      </div>
      {/* Chat */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
      </svg>
      {/* Profile */}
      <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    </div>
  </div>
);
